import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Chip,
    styled,
} from '@mui/material';
import {
    Add as AddIcon,
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import Constants from '../../../assests/Constants/constants';

// Styled components
const StyledModalHeader = styled(Box)(({ theme, direction }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
}));

const StyledModalTitle = styled(Typography)(({ theme, direction }) => ({
    color: theme.palette.text.primary,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
}));

const DocumentChoiceModal = ({ 
    visible, 
    onClose, 
    onInvoiceCreator, 
    onReceiptCreator, 
    isTaxExempt,
    client
}) => {
    const { t, i18n } = useTranslation();
    const modalRef = useRef();
    const direction = i18n.dir();
    const isLawyer = client?.businessField === Constants.Lawyer;

    const handleOverlayClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose(e);
        }
    };

    if (!visible) return null;

    return (
        <div className='create-modal-overlay' onClick={handleOverlayClick}>
            <div className="modal-background">
                <div className='modal-Invoice-choice-content' ref={modalRef}>
                    <StyledModalHeader dir={direction}>
                        <StyledModalTitle variant="h5" dir={direction}>
                            <AddIcon color="primary" />
                            {t('newDocument')}
                        </StyledModalTitle>
                        <Chip
                            label={t('SelectDocumentType')}
                            color="primary"
                            size="small"
                        />
                    </StyledModalHeader>
                    <div className="document-cards-grid">
                        {!isTaxExempt && (
                            <>
                                {isLawyer ? (
                                    <>
                                        <div className="document-card" onClick={() => onInvoiceCreator('LawyerInvoice')}>
                                            <div className="card-icon">
                                                <DescriptionOutlinedIcon color="white" fontSize="large" />
                                            </div>
                                            <div className="card-title">{t('LawyerInvoice')}</div>
                                        </div>
                                        <div className="document-card" onClick={() => onReceiptCreator('LawyerReceipt')}>
                                            <div className="card-icon">
                                                <ReceiptLongOutlinedIcon color="white" fontSize="large" />
                                            </div>
                                            <div className="card-title">{t('LawyerReceipt')}</div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="document-card" onClick={() => onInvoiceCreator('TaxInvoice')}>
                                            <div className="card-icon">
                                                <DescriptionOutlinedIcon color="white" fontSize="large" />
                                            </div>
                                            <div className="card-title">{t('TaxInvoice')}</div>
                                        </div>
                                        <div className="document-card" onClick={() => onReceiptCreator('ReceiptTax')}>
                                            <div className="card-icon">
                                                <ReceiptLongOutlinedIcon color="white" fontSize="large" />
                                            </div>
                                            <div className="card-title">{t('ReceiptTax')}</div>
                                        </div>
                                    </>
                                )}
                                <div className="document-card" onClick={() => onInvoiceCreator('CreditNote')}>
                                    <div className="card-icon">
                                        <AssignmentReturnOutlinedIcon color="white" fontSize="large" />
                                    </div>
                                    <div className="card-title">{t('CreditNote')}</div>
                                </div>
                            </>
                        )}
                        <div className="document-card" onClick={() => onReceiptCreator('Receipt')}>
                            <div className="card-icon">
                                <ReceiptOutlinedIcon color="white" fontSize="large" />
                            </div>
                            <div className="card-title">{t('Receipt')}</div>
                        </div>
                        <div className="document-card" onClick={() => onInvoiceCreator('Quotation')}>
                            <div className="card-icon">
                                <RequestQuoteOutlinedIcon color="white" fontSize="large" />
                            </div>
                            <div className="card-title">{t('Quotation')}</div>
                        </div>
                        <div className="document-card" onClick={() => onInvoiceCreator('Order')}>
                            <div className="card-icon">
                                <ShoppingCartOutlinedIcon color="white" fontSize="large" />
                            </div>
                            <div className="card-title">{t('Order')}</div>
                        </div>
                        <div className="document-card" onClick={() => onInvoiceCreator('Transaction')}>
                            <div className="card-icon">
                                <CompareArrowsOutlinedIcon color="white" fontSize="large" />
                            </div>
                            <div className="card-title">{t('Transaction')}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

DocumentChoiceModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onInvoiceCreator: PropTypes.func.isRequired,
    onReceiptCreator: PropTypes.func.isRequired,
    isTaxExempt: PropTypes.bool,
    client: PropTypes.object,
};

DocumentChoiceModal.defaultProps = {
    isTaxExempt: false,
    client: null,
};

export default DocumentChoiceModal;
